import { Tooltip, styled } from '@mui/material';
import addPatternIconUrl from '@resources/img/create/design-lab-v4/icons/add-pattern.svg?url';
import addTextIconUrl from '@resources/img/create/design-lab-v4/icons/add-text.svg?url';
import importDesignIconUrl from '@resources/img/create/design-lab-v4/icons/import-design.svg?url';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../../Common/Buttons/IconButton';
import DesignBrowser from '../../Common/_components/DesignBrowser';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import { AddPatternLayer, AddTextLayer, EditActiveOption, EditActiveVariant, EditAutoDesignSubproduct, EditLayer, EditMirrorMode, RemoveLayer, SelectLayer } from '../../UIData/_actions/DesignLabStoreActions';
import { EditLoader } from '../../UIData/_actions/UIDataActions';
import { FetchOneItem, FetchPaginationItems } from '../../UserData/_actions/UserDataActions';
import { UserProductDesignPayload, UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore';
import { LAB_TEMPLATE_HEIGHT, LAB_TEMPLATE_WIDTH } from '../../config';
import AddImageTool from '../Tool/_components/AddImageTool';
import PatternBrowser from '../_components/PatternBrowser';
import useLabData from '../_hooks/useLabData';
import type PatternStore from '../_stores/PatternStore';
import ImportDesignDialog from './ImportDesignDialog';
import LayerList from './LayerList';

export const ListTitle = styled('h3')(({ theme }) => ({
    marginTop: 0,
    padding: '0 15px 0',
    fontSize: '16px',
    textTransform: 'uppercase',
    lineHeight: '40px',
    color: '#333',
    marginBottom: 0,
    
    ['@media (max-width: 1510px) and (min-height: ' + theme.height_breakpoints.values.lab_mobile + 'px), (max-height: 1070px) and (max-width: 953px)']: {
        lineHeight: '50px',
    }
}));

const IMPORT_DESIGN_CHECK_LIST_ID = 'design-lab-import-check';
const PRINTED_PATCH_CATEGORY_ID = 43;

const LayerListContainer = styled('div')(({ theme }) => ({
    width: '275px',
    flexShrink: 0,
    transition: 'transform 300ms, opacity 300ms',
    position: 'relative',
    zIndex: 30,
    opacity: 1,
    paddingRight: '10px',
    
    '&.hidden': {
        transform: 'translateX(-100px)',
        opacity: 0,
    },
    
    ['@media (max-width: 1510px) and (min-height: ' + theme.height_breakpoints.values.lab_mobile + 'px), (max-height: 1070px) and (max-width: 953px)']: {
        height: '236px',
        marginBottom: '10px',
        width: 'auto',
        position: 'relative',
    },
    
    ['@media (max-width: ' + theme.breakpoints.values.lab_mobile + 'px), (max-height: ' + theme.height_breakpoints.values.lab_mobile + 'px)']: {
        transform: 'translateX(-100px)',
        opacity: 0,
        
        '&.active': {
            opacity: 1,
            transform: 'none',
        },
    }
}));

const ListWrap = styled('div')(({ theme }) => ({
    padding: '10px 10px 30px 15px',
    height: 'calc(100dvh - var(--design-lab-content-top, 74px) - 98px)',
    overflow: 'auto',
    
    ['@media (max-width: 1510px) and (min-height: ' + theme.height_breakpoints.values.lab_mobile + 'px), (max-height: 1070px) and (max-width: 953px)']: {
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        position: 'absolute',
        overflow: 'auto',
        width: '100%',
        height: 'calc(100% - 50px)',
        top: '50px',
        padding: '10px 15px 0',
        left: '0',
    },
    
    '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
    },
    
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        borderRadius: '10px',
        
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
        },
    }
}));

const AddContainer = styled('div')(({ theme }) => ({
    marginBottom: '10px',
    display: 'flex',
    gap: '10px',
    padding: '0 15px',
    
    ['@media (max-width: 1510px) and (min-height: ' + theme.height_breakpoints.values.lab_mobile + 'px), (max-height: 1070px) and (max-width: 953px)']: {
        position: 'absolute',
        top: '5px',
        right: '0',
    },
}));

interface Props {
    hidden?: boolean
    active?: boolean
}

export default function LayerSection(props: Props) {
    const dispatch = useAppDispatch();
    const [t] = useTranslation('design-lab');
    
    const activeScene = useAppSelector(state => state.get('UIData').get('designLab').get('activeScene'));
    const activeSubproduct = useAppSelector(state => state.get('UIData').get('designLab').get('activeSubproduct'));
    const activeProductId = useAppSelector(state => state.get('UIData').get('designLab').get('activeProductId'));
    const autoDesign = useAppSelector(state => activeSubproduct !== null ? state.get('UIData').get('designLab').get('autoDesignSubproducts').get(activeSubproduct) : undefined);
    const [patternBrowserOpened, setPatternBrowserOpened] = useState(false);
    const labData = useLabData()
    const productData = useAppSelector(state => state.get('appData').get('products').get(String(activeProductId)));
    const importedProductData = useAppSelector(state => labData ? state.get('appData').get('products').get(String(labData.imports)) : undefined);
    const importedProductViewCategory = useAppSelector(state => importedProductData ? importedProductData?.get('viewCategories').first() : undefined)
    const layers = useAppSelector(state => state.get('UIData').get('designLab').get('layers'));
    const [importPromptOpened, setImportPromptOpened] = useState<boolean>(false)
    const [importBrowserOpened, setImportBrowserOpened] = useState<boolean>(false)
    const importPromptDismissed = useRef(false)
    
    const onSelectPattern = useCallback((item: PatternStore) => {
        if(!activeScene) return;
        
        const originalWidth = item.get('repeat_width');
        const originalHeight = item.get('repeat_height');
        const width = originalWidth;
        const height = originalHeight;
        const scale = Math.min(LAB_TEMPLATE_WIDTH / width / item.get('horizontalRapportsPostcard'), LAB_TEMPLATE_HEIGHT / height / item.get('horizontalRapportsPostcard'));
        
        dispatch(AddPatternLayer({
            fileid: item.get('id'),
            src: item.get('repeat_src'),
            originalWidth,
            originalHeight,
            scene: activeScene,
            subproduct: activeSubproduct,
            width,
            height,
            x: LAB_TEMPLATE_WIDTH / 2,
            y: LAB_TEMPLATE_HEIGHT / 2,
            scaleX: scale,
            scaleY: scale,
            rotation: 0,
        }))
    }, [activeScene, activeSubproduct, dispatch])
    
    const onPatternBrowserOpen = useCallback(() => {
        setPatternBrowserOpened(true)
    }, [])
    
    const onPatternBrowserClose = useCallback(() => {
        setPatternBrowserOpened(false);
    }, [])

    const onImportPromptClose = useCallback(() => {
        importPromptDismissed.current = true
        setImportPromptOpened(false)
    }, [])

    const onImportBrowserOpen = useCallback(() => {
        importPromptDismissed.current = true
        setImportPromptOpened(false)
        setImportBrowserOpened(true)
    }, [])
    
    const onAddText = useCallback(() => {
        if(!activeScene) return;
        
        dispatch(AddTextLayer({
            text: t('Your text'),
            color: "#000000",
            font: "Norican",
            scene: activeScene,
            subproduct: activeSubproduct,
            x: LAB_TEMPLATE_WIDTH / 2,
            y: LAB_TEMPLATE_HEIGHT / 2,
            width: 0,
            height: 0,
            scaleX: 1,
            scaleY: 1,
            rotation: 0,
        }))
    }, [activeScene, activeSubproduct, dispatch, t])

    // Currently importing products will assume the variants are 1:1 between the two, using slug to map.
    // Options and settings like auto-design and mirrormodes as well.
    const onImportDesignSelect = useCallback((design: UserProductDesignStore) => {
        if (!productData || !importedProductData) return
        
        const imported_variant_id = design.get('id_product_variant')
        const imported_variant = importedProductData.get('variants').get(String(imported_variant_id))
        
        if(!imported_variant) return
        
        dispatch(EditLoader("design-lab_temp", true));
        
        dispatch(FetchOneItem(design.get('id'), 'productDesigns', ['designData']).set({
            onSuccess: (response, dispatch) => {
                dispatch(EditActiveVariant(imported_variant.get('slug')));
                
                const designData = (response.data.item as UserProductDesignPayload).designData

                layers.forEach(layer => {
                    dispatch(RemoveLayer(layer.get('id')));
                });

                let selectedLayer: string | null = null;
                Object.keys(designData.layers).forEach(layerId => {
                    //Select first layer
                    if (!selectedLayer) { selectedLayer = layerId }
                    dispatch(EditLayer(layerId, designData.layers[layerId]));
                });
                if (selectedLayer) dispatch(SelectLayer(selectedLayer));
                
                //Load mirrorModes
                if (designData.mirrorModes) {
                    Object.keys(designData.mirrorModes).forEach(key => {
                        dispatch(EditMirrorMode(key, designData.mirrorModes[key]));
                    });
                }
                
                //Load autoDesign
                if (designData.autoDesignSubproducts) {
                    Object.keys(designData.autoDesignSubproducts).forEach(key => {
                        dispatch(EditAutoDesignSubproduct(key, designData.autoDesignSubproducts[key]));
                    });
                }
                
                //Load options
                if (designData.options) {
                    Object.keys(designData.options).forEach(key => {
                        dispatch(EditActiveOption(key, designData.options[key]));
                    });
                }
                
                dispatch(EditLoader("design-lab_temp", false));
                setImportBrowserOpened(false)
            },
            onFailure: () => {
                // Show error something something ?
                dispatch(EditLoader("design-lab_temp", false));
                setImportBrowserOpened(false)
            },
        }));
    }, [layers, activeProductId, productData, importedProductData])

    useEffect(() => {
        if (labData?.imports === undefined) return

        let cancelled = false
        importPromptDismissed.current = false
        setImportPromptOpened(false)

        const data = new FormData()
        data.append('offset', '0')
        data.append('limit', '1')
        data.append('categories', String(PRINTED_PATCH_CATEGORY_ID))

        dispatch(FetchPaginationItems(IMPORT_DESIGN_CHECK_LIST_ID, data, 'productDesigns').set({
            onSuccess: response => {
                if (cancelled || importPromptDismissed.current) return
                setImportPromptOpened(response.data.totalItems > 0)
            },
            onFailure: () => {
                if (!cancelled) setImportPromptOpened(false)
            },
        }))

        return () => {
            cancelled = true
        }
    }, [activeProductId, dispatch, labData?.imports])

    return <LayerListContainer className={props.hidden ? 'hidden' : (props.active ? 'active' : '')}>
        <ListTitle>{t('Layers')}</ListTitle>
        <AddContainer>
            {labData?.imports ?
                <Tooltip title={t(`Import ${importedProductData?.get('name')}`)}>
                <IconButton
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={onImportBrowserOpen}
                ><img src={importDesignIconUrl} /></IconButton>
                </Tooltip>
                :
                <></>
            }
            <AddImageTool />
            <Tooltip title={t('Add Pattern')}>
                <IconButton
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={!!autoDesign}
                    onClick={onPatternBrowserOpen}
                ><img src={addPatternIconUrl} /></IconButton>
            </Tooltip>
            <Tooltip title={t('Add Text')}>
                <IconButton
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={!!autoDesign}
                    onClick={onAddText}
                ><img src={addTextIconUrl} /></IconButton>
            </Tooltip>
        </AddContainer>
        <ListWrap>
            <LayerList/>
        </ListWrap>
        <PatternBrowser
            opened={patternBrowserOpened}
            onSelect={onSelectPattern}
            onClose={onPatternBrowserClose}
        />
        {labData?.imports && !props.hidden ? <>
            <DesignBrowser onSelect={onImportDesignSelect} opened={importBrowserOpened} category={importedProductViewCategory} onClose={() => setImportBrowserOpened(false)}/>
            <ImportDesignDialog
                open={importPromptOpened}
                title={t('Import a Printed Patch?')}
                selectExistingLabel={t('Select Existing Patch')}
                startBlankLabel={t('Start Blank')}
                onClose={onImportPromptClose}
                onSelectExisting={onImportBrowserOpen}
                onStartBlank={onImportPromptClose}
            />
            </>
            : 
            <></>
        }
    </LayerListContainer>
}
